import { Fragment, useEffect, useState } from 'react'
import { Building2, Calendar, Car, Fuel, Layers, Tags, Network, ListChecks, Gauge, Percent, FileText, FileSpreadsheet } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'
import { SeletorMultiplo } from './RelatorioConsumoArla'

type Classe = 'Excelente' | 'Bom' | 'Regular' | 'Ruim' | 'Péssimo'
type Situacao = Classe | 'Sem percurso' | 'Verificar' | 'Sem prevista'
interface Faixas { excelente: number; bom: number; regular: number; ruim: number }
interface Abastecimento {
  data: string; hora: number; hodometro: number; litros: number; valor: number; km: number | null
  media: number | null; desvio: number | null; motorista: string; origem: string; situacao: Situacao; obs: string
}
interface Veiculo {
  veicod: string; placa: string; tipo: string; modeloAgr: string; classeVei: string; prevista: number | null; itens: Abastecimento[]
  km: number; litros: number; valor: number; media: number | null; desvio: number | null; situacao: Situacao; resultado: number | null
}
interface Motorista {
  motorista: string; abastecimentos: number; veiculos: number; km: number; litros: number; media: number | null
  prevista: number | null; desvio: number | null; resultado: number; porClasse: Record<Classe, number>
}
interface Resultado { veiculos: Veiculo[]; motoristas: Motorista[]; contagem: Record<Situacao, number>; totais: { km: number; litros: number; valor: number; media: number | null; resultado: number } }
interface Opcao { cod: string; des: string }

const CLASSES: Classe[] = ['Excelente', 'Bom', 'Regular', 'Ruim', 'Péssimo']
const EXTRAS: Situacao[] = ['Verificar', 'Sem percurso', 'Sem prevista']
const COR: Record<Situacao, string> = {
  Excelente: '#15803d', Bom: '#65a30d', Regular: '#ca8a04', Ruim: '#ea580c', 'Péssimo': '#b91c1c',
  'Sem percurso': '#94a3b8', Verificar: '#7c3aed', 'Sem prevista': '#94a3b8',
}
const fmt = (v: number | null | undefined, c: number) => (v == null ? '' : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c }))
const fmtPct = (v: number | null) => (v == null ? '' : `${v > 0 ? '+' : ''}${fmt(v, 1)}%`)
const br = (d: string) => d.split('-').reverse().join('/')
const inicioMes = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10) }
const hojeIso = () => new Date().toISOString().slice(0, 10)

function Selo({ s }: { s: Situacao }) {
  return <span style={{ display: 'inline-block', padding: '0.05rem 0.45rem', borderRadius: 999, fontSize: '0.68rem', fontWeight: 800, color: COR[s], background: `${COR[s]}1a`, border: `1px solid ${COR[s]}55`, whiteSpace: 'nowrap' }}>{s}</span>
}

// Desvio de consumo por veículo (GeneXus WOMEDVEX → RDPS036m), com resumo por motorista.
export default function RelatorioDesvioConsumo() {
  const { empresas } = useEmpresa()
  const [dataIni, setDataIni] = useState(inicioMes())
  const [dataFim, setDataFim] = useState(hojeIso())
  const [veiculo, setVeiculo] = useState('')
  const [tiposSel, setTiposSel] = useState<string[]>([])
  const [modelosSel, setModelosSel] = useState<string[]>([])
  const [classesSel, setClassesSel] = useState<string[]>([])
  const [empresasSel, setEmpresasSel] = useState<string[]>([])
  const [combSel, setCombSel] = useState<string[]>([])
  const [faixas, setFaixas] = useState<Faixas>({ excelente: 6, bom: 0, regular: 9, ruim: 14 })
  const [mostrar, setMostrar] = useState<Situacao[]>([...CLASSES, ...EXTRAS])
  const [tipoRel, setTipoRel] = useState<'I' | 'S'>('I')
  const [mediaAgrupamento, setMediaAgrupamento] = useState(false)
  const [tipos, setTipos] = useState<Opcao[]>([])
  const [modelos, setModelos] = useState<Opcao[]>([])
  const [classesVei, setClassesVei] = useState<Opcao[]>([])
  const [combustiveis, setCombustiveis] = useState<Opcao[]>([])
  const [res, setRes] = useState<Resultado | null>(null)
  const [resTipo, setResTipo] = useState<'I' | 'S'>('I')
  const [aba, setAba] = useState<'veiculos' | 'motoristas'>('veiculos')
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  useEffect(() => {
    api.get('/combustivel/tipos-veiculo').then(r => setTipos(r.data)).catch(() => {})
    api.get('/combustivel/modelos-agrupamento').then(r => setModelos(r.data)).catch(() => {})
    api.get('/combustivel/classes-veiculo').then(r => setClassesVei(r.data)).catch(() => {})
    api.get('/combustivel/combustiveis-veiculo').then(r => setCombustiveis(r.data)).catch(() => {})
    api.get('/combustivel/relatorios/desvio-consumo/faixas').then(r => setFaixas(r.data)).catch(() => {})
  }, [])

  const params = () => ({
    dataIni, dataFim, veiculo, tipos: tiposSel.join(','), modelos: modelosSel.join(','), classesVei: classesSel.join(','),
    empresas: empresasSel.join(','), combustiveis: combSel.join(','), ...faixas, mostrar: mostrar.join(','), tipoRel,
    mediaAgrupamento: mediaAgrupamento ? '1' : '',
  })

  async function gerar() {
    setErro(''); setCarregando(true)
    try { setRes((await api.get('/combustivel/relatorios/desvio-consumo', { params: params() })).data); setResTipo(tipoRel) }
    catch (e: any) { setRes(null); setErro(e?.response?.data?.error || 'Erro ao gerar relatório.') }
    finally { setCarregando(false) }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro(''); setGerando(formato)
    try {
      // PDF/Excel seguem a aba aberta: veículos/abastecimentos ou só o resumo por motorista.
      const visao = res && aba === 'motoristas' ? 'motoristas' : 'veiculos'
      const r = await api.get(`/combustivel/relatorios/desvio-consumo/${formato}`, { params: { ...params(), visao }, responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') window.open(url, '_blank')
      else {
        const a = document.createElement('a'); a.href = url
        a.download = `desvio-consumo-${visao === 'motoristas' ? 'por-motorista' : tipoRel === 'S' ? 'sintetico' : 'individual'}.xlsx`
        a.click(); URL.revokeObjectURL(url)
      }
    } catch (e: any) {
      // Erro de validação vem como blob JSON.
      try { const t = await e?.response?.data?.text?.(); setErro(JSON.parse(t).error) } catch { setErro(formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.') }
    } finally { setGerando('') }
  }

  const alternar = (s: Situacao) => setMostrar(m => (m.includes(s) ? m.filter(x => x !== s) : [...m, s]))
  const campoPct = (k: keyof Faixas) => (
    <input type="number" min={0} max={100} value={faixas[k]} onChange={e => setFaixas(f => ({ ...f, [k]: Number(e.target.value) || 0 }))}
      style={{ width: 60, textAlign: 'right' }} />
  )

  const th: React.CSSProperties = { padding: '0.4rem 0.45rem', fontSize: '0.66rem', fontWeight: 800, color: '#fff', background: '#1a6ea8', whiteSpace: 'nowrap', textAlign: 'right', position: 'sticky', top: 0, zIndex: 1 }
  const td: React.CSSProperties = { padding: '0.28rem 0.45rem', fontSize: '0.74rem', borderBottom: '1px solid #eef2f7', whiteSpace: 'nowrap', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
  const esq: React.CSSProperties = { ...td, textAlign: 'left' }
  const corDesvio = (d: number | null) => (d == null ? '#475569' : d >= 0 ? '#15803d' : '#b91c1c')
  const tabBtn = (id: 'veiculos' | 'motoristas', rotulo: string) => (
    <button onClick={() => setAba(id)} className="btn" style={{ padding: '0.35rem 0.9rem', fontSize: '0.78rem', fontWeight: 700, background: aba === id ? '#1b3f7b' : '#e2e8f0', color: aba === id ? '#fff' : '#1f2937' }}>{rotulo}</button>
  )
  const visiveis = res ? (resTipo === 'S' ? res.veiculos.filter(v => mostrar.includes(v.situacao)) : res.veiculos) : []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 720, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <Gauge size={18} color="#fff" />
            </span>
            <div>
              <h2>Desvio de Consumo</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Média de cada abastecimento x média prevista do veículo, com resumo por motorista</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Período" icon={Calendar}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} style={{ maxWidth: 170 }} />
              <span style={{ fontSize: '0.8rem', color: '#57606f' }}>a</span>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={{ maxWidth: 170 }} />
            </div>
          </Linha>
          <Linha label="Veículo" icon={Car}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ maxWidth: 140 }}>
                <LookupSelect endpoint="/erp/veiculos" codKey="veicod" descKey="veipla" tagKey="veicod"
                  codValue={veiculo} descValue={veiculo} onSelect={cod => setVeiculo(cod)} placeholder="Todos" />
              </div>
              {veiculo
                ? <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }} onClick={() => setVeiculo('')}>Todos</button>
                : <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todos</span>}
            </div>
          </Linha>
          <Linha label="Tipos" icon={Tags}>
            <SeletorMultiplo opcoes={tipos} selecionados={tiposSel} onChange={setTiposSel} placeholderBusca="Buscar tipo..." />
          </Linha>
          <Linha label="Mod. Agrupamento" icon={Layers}>
            <SeletorMultiplo opcoes={modelos} selecionados={modelosSel} onChange={setModelosSel} />
          </Linha>
          <Linha label="Equipe" icon={Network}>
            <SeletorMultiplo opcoes={classesVei} selecionados={classesSel} onChange={setClassesSel} placeholderBusca="Buscar classe..." />
          </Linha>
          <Linha label="Empresas" icon={Building2}>
            <SeletorMultiplo opcoes={empresas.map(e => ({ cod: String(e.empcod), des: `${e.empcod} — ${e.empfan?.trim() || e.empnom?.trim()}` }))}
              selecionados={empresasSel} onChange={setEmpresasSel} placeholderBusca="Buscar empresa..." />
          </Linha>
          <Linha label="Combustível" icon={Fuel}>
            <SeletorMultiplo opcoes={combustiveis} selecionados={combSel} onChange={setCombSel} placeholderBusca="Buscar combustível..." />
          </Linha>
          <Linha label="Média Prevista" icon={Gauge}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.8rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={mediaAgrupamento} onChange={e => setMediaAgrupamento(e.target.checked)} />
              Média pelo agrupamento
              <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>(sem média no agrupamento, usa a do cadastro do veículo)</span>
            </label>
          </Linha>
          <Linha label="% de Desvio" icon={Percent}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '0.35rem 0.7rem', alignItems: 'center', fontSize: '0.8rem' }}>
              <span><b style={{ color: COR.Excelente }}>Excelente</b> acima de +</span><span>{campoPct('excelente')} %</span>
              <span><b style={{ color: COR.Bom }}>Bom</b> acima de +</span><span>{campoPct('bom')} %</span>
              <span><b style={{ color: COR.Regular }}>Regular</b> até −</span><span>{campoPct('regular')} %</span>
              <span><b style={{ color: COR.Ruim }}>Ruim</b> até −</span><span>{campoPct('ruim')} %</span>
              <span><b style={{ color: COR['Péssimo'] }}>Péssimo</b> abaixo de −{faixas.ruim}%</span><span />
            </div>
            <div style={{ fontSize: '0.72rem', marginTop: 4, color: '#94a3b8' }}>Percentual sobre a média prevista do cadastro do veículo.</div>
          </Linha>
          <Linha label="Exibir" icon={ListChecks}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.9rem' }}>
              {[...CLASSES, ...EXTRAS].map(s => (
                <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', cursor: 'pointer', textTransform: 'none' }}>
                  <input type="checkbox" style={{ width: 'auto', margin: 0 }} checked={mostrar.includes(s)} onChange={() => alternar(s)} />
                  <span style={{ color: COR[s], fontWeight: 700 }}>{s}</span>
                </label>
              ))}
            </div>
          </Linha>
          <Linha label="Tipo Relatório" icon={ListChecks}>
            <select value={tipoRel} onChange={e => setTipoRel(e.target.value as 'I' | 'S')} style={{ maxWidth: 220 }}>
              <option value="I">Individual (cada abastecimento)</option>
              <option value="S">Sintético (por veículo)</option>
            </select>
          </Linha>
          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>{erro}</div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }} onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }} onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo" style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {[...CLASSES, ...EXTRAS].map(s => (
                <div key={s} style={{ minWidth: 92, padding: '0.4rem 0.6rem', borderRadius: 8, borderTop: `4px solid ${COR[s]}`, background: '#fff', boxShadow: '0 1px 3px rgba(15,23,42,0.08)' }}>
                  <div style={{ fontSize: '0.64rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>{s}</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>{res.contagem[s] || 0}</div>
                </div>
              ))}
              <div style={{ minWidth: 150, padding: '0.4rem 0.6rem', borderRadius: 8, borderTop: '4px solid #1b3f7b', background: '#fff', boxShadow: '0 1px 3px rgba(15,23,42,0.08)' }}>
                <div style={{ fontSize: '0.64rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Economia x prevista</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 800, color: res.totais.resultado >= 0 ? '#15803d' : '#b91c1c' }}>R$ {fmt(res.totais.resultado, 2)}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.4rem' }}>{tabBtn('veiculos', resTipo === 'S' ? 'Por veículo' : 'Abastecimentos')}{tabBtn('motoristas', `Por motorista (${res.motoristas.length})`)}</div>

            {aba === 'veiculos' && (
              <div style={{ overflow: 'auto', maxHeight: '70vh', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                  <thead>
                    {resTipo === 'S' ? (
                      <tr>{['Veículo', 'Placa', 'Tipo', 'Mod. Agrupamento', 'Km', 'Litros', 'Valor', 'Média', 'Prevista', 'Desvio', 'Economia (R$)', 'Equipe', 'Classe'].map(c => (
                        <th key={c} style={{ ...th, textAlign: ['Veículo', 'Placa', 'Tipo', 'Mod. Agrupamento', 'Equipe', 'Classe'].includes(c) ? 'left' : 'right' }}>{c}</th>))}</tr>
                    ) : (
                      <tr>{['Data', 'Veículo', 'Placa', 'Litros', 'Hodômetro', 'Percurso', 'Média', 'Prevista', 'Desvio', 'Motorista', 'Observação', 'Classe'].map(c => (
                        <th key={c} style={{ ...th, textAlign: ['Data', 'Veículo', 'Placa', 'Motorista', 'Observação', 'Classe'].includes(c) ? 'left' : 'right' }}>{c}</th>))}</tr>
                    )}
                  </thead>
                  <tbody>
                    {resTipo === 'S' && visiveis.map((v, k) => (
                      <tr key={v.veicod} style={{ background: k % 2 ? '#f5f9fd' : '#fff' }}>
                        <td style={{ ...esq, fontWeight: 700, color: '#1a6ea8' }}>{v.veicod}</td>
                        <td style={{ ...esq, color: '#b91c1c' }}>{v.placa}</td>
                        <td style={esq}>{v.tipo}</td>
                        <td style={esq}>{v.modeloAgr}</td>
                        <td style={td}>{fmt(v.km, 0)}</td>
                        <td style={td}>{fmt(v.litros, 3)}</td>
                        <td style={td}>{fmt(v.valor, 2)}</td>
                        <td style={{ ...td, fontWeight: 700 }}>{fmt(v.media, 2)}</td>
                        <td style={td}>{fmt(v.prevista, 2)}</td>
                        <td style={{ ...td, color: corDesvio(v.desvio), fontWeight: 700 }}>{fmtPct(v.desvio)}</td>
                        <td style={{ ...td, color: (v.resultado ?? 0) >= 0 ? '#15803d' : '#b91c1c' }}>{fmt(v.resultado, 2)}</td>
                        <td style={{ ...esq, fontWeight: 700 }}>{v.classeVei}</td>
                        <td style={esq}><Selo s={v.situacao} /></td>
                      </tr>
                    ))}
                    {resTipo === 'I' && visiveis.map(v => {
                      const itens = v.itens.filter(i => mostrar.includes(i.situacao))
                      return (
                        <Fragment key={v.veicod}>
                          {itens.map((i, k) => (
                            <tr key={`${v.veicod}-${k}`} style={{ background: k % 2 ? '#f5f9fd' : '#fff' }}>
                              <td style={{ ...esq, color: '#1a6ea8' }}>{br(i.data)}</td>
                              <td style={{ ...esq, fontWeight: 700, color: '#1a6ea8' }}>{v.veicod}</td>
                              <td style={{ ...esq, color: '#b91c1c' }}>{v.placa}</td>
                              <td style={td}>{fmt(i.litros, 3)}</td>
                              <td style={td}>{fmt(i.hodometro, 0)}</td>
                              <td style={td}>{fmt(i.km, 0)}</td>
                              <td style={{ ...td, fontWeight: 700 }}>{fmt(i.media, 2)}</td>
                              <td style={td}>{fmt(v.prevista, 2)}</td>
                              <td style={{ ...td, color: corDesvio(i.desvio), fontWeight: 700 }}>{fmtPct(i.desvio)}</td>
                              <td style={esq}>{i.motorista}</td>
                              <td style={{ ...esq, color: '#64748b', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }} title={i.obs}>{i.obs || (i.origem === 'Nota' ? 'nota fiscal' : '')}</td>
                              <td style={esq}><Selo s={i.situacao} /></td>
                            </tr>
                          ))}
                        </Fragment>
                      )
                    })}
                    {visiveis.length === 0 && <tr><td colSpan={resTipo === 'S' ? 13 : 12} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum registro no período.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}

            {aba === 'motoristas' && (
              <>
                <div style={{ fontSize: '0.74rem', color: '#64748b' }}>
                  Média real = km ÷ litros de todos os abastecimentos classificados do motorista (sem os marcados como "Verificar"). Prevista ponderada pelo km rodado em cada veículo.
                </div>
                <div style={{ overflow: 'auto', maxHeight: '70vh', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                  <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                    <thead>
                      <tr>
                        {['#', 'Motorista', 'Veíc.', 'Abast.', 'Km', 'Litros', 'Média', 'Prevista', 'Desvio', ...CLASSES, 'Economia (R$)'].map(c => (
                          <th key={c} style={{ ...th, textAlign: c === 'Motorista' ? 'left' : 'right', background: (CLASSES as string[]).includes(c) ? COR[c as Classe] : '#1a6ea8' }}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {res.motoristas.map((m, k) => (
                        <tr key={`${m.motorista}-${k}`} style={{ background: k % 2 ? '#f5f9fd' : '#fff' }}>
                          <td style={{ ...td, color: '#94a3b8' }}>{k + 1}</td>
                          <td style={{ ...esq, fontWeight: 700 }}>{m.motorista}</td>
                          <td style={td}>{m.veiculos}</td>
                          <td style={td}>{m.abastecimentos}</td>
                          <td style={td}>{fmt(m.km, 0)}</td>
                          <td style={td}>{fmt(m.litros, 3)}</td>
                          <td style={{ ...td, fontWeight: 700 }}>{fmt(m.media, 2)}</td>
                          <td style={td}>{fmt(m.prevista, 2)}</td>
                          <td style={{ ...td, color: corDesvio(m.desvio), fontWeight: 800 }}>{fmtPct(m.desvio)}</td>
                          {CLASSES.map(c => <td key={c} style={{ ...td, color: m.porClasse[c] ? COR[c] : '#cbd5e1', fontWeight: 700 }}>{m.porClasse[c] || '·'}</td>)}
                          <td style={{ ...td, color: m.resultado >= 0 ? '#15803d' : '#b91c1c', fontWeight: 700 }}>{fmt(m.resultado, 2)}</td>
                        </tr>
                      ))}
                      {res.motoristas.length === 0 && <tr><td colSpan={15} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum abastecimento classificado.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
